// @ts-nocheck

'use client';

import React, { useContext, useEffect, useCallback, useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import type { Doc as YDoc } from 'yjs';
import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';

// --- Tiptap Core Extensions ---
import { StarterKit } from '@tiptap/starter-kit';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';

import { Collaboration, isChangeOrigin } from '@tiptap/extension-collaboration';
import { CollaborationCaret } from '@tiptap/extension-collaboration-caret';
import { Typography } from '@tiptap/extension-typography';
import { Highlight } from '@tiptap/extension-highlight';
import { Superscript } from '@tiptap/extension-superscript';
import { Subscript } from '@tiptap/extension-subscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { Ai } from '@tiptap-pro/extension-ai';
import AiChanges from '@tiptap-pro/extension-ai-changes';
import { Decoration } from '@tiptap/pm/view';
import { ExportDocx } from '@tiptap-pro/extension-export-docx';
import { Snapshot } from '@tiptap-pro/extension-snapshot';
import { CommentsKit } from '@tiptap-pro/extension-comments';
import { UniqueID } from '@tiptap/extension-unique-id';
import { Emoji, gitHubEmojis } from '@tiptap/extension-emoji';
import { getHierarchicalIndexes, TableOfContents } from '@tiptap/extension-table-of-contents';
import { Mathematics } from '@tiptap/extension-mathematics';
import { Pages } from '@tiptap-pro/extension-pages';
import { PageBreak } from '@tiptap-pro/extension-pagebreak';

import Logger from 'utils/logger';

import { Selection } from 'containers/Tiptap/components/tiptap-extension/selection-extension';
import { pruneToSchema } from 'containers/Tiptap/lib/import-sanitize';
import { writeImportedHeaderFooter } from 'containers/Tiptap/lib/import-header-footer';
import {
  DocumentViewMode,
  writeDocumentSettings,
  writeNumberingFormats,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

// --- Hooks ---
import { useUiEditorState } from 'containers/Tiptap/hooks/use-ui-editor-state';
import { useScrollToHash } from 'containers/Tiptap/components/tiptap-ui/copy-anchor-link-button/use-scroll-to-hash';

// --- Editor config ---
import 'Tiptap/editor-config/extensions/core-node-styles';
import { InlineCode } from 'Tiptap/editor-config/extensions/inline-code';
import { createConvertKitExtension } from 'Tiptap/editor-config/extensions/convert-kit';
import { NumberingFormatsSync } from 'Tiptap/editor-config/extensions/numbering-formats-sync';
import { BulletListFormats } from 'Tiptap/editor-config/extensions/bullet-list-formats';
import { numberingFormats } from 'Tiptap/editor-config/numbering-formats';

// --- Custom Extensions ---
import { HorizontalRule } from 'containers/Tiptap/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import { UiState } from 'containers/Tiptap/components/tiptap-extension/ui-state-extension';
import { Image } from 'containers/Tiptap/components/tiptap-node/image-node/image-node-extension';
import { NodeBackground } from 'containers/Tiptap/components/tiptap-extension/node-background-extension';
import { NodeAlignment } from 'containers/Tiptap/components/tiptap-extension/node-alignment-extension';
import { TocNode } from 'containers/Tiptap/components/tiptap-node/toc-node/extensions/toc-node-extension';
import { FileDropExtension } from 'containers/Tiptap/components/tiptap-extension/file-drop-extension';

// --- Tiptap Node ---
import { ImageUploadNode } from 'containers/Tiptap/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { FileUploadProvider } from 'containers/Tiptap/components/tiptap-node/image-upload-node/image-upload-node';
import { HeaderFooterEditing, PageEdgeStyles } from 'containers/Tiptap/components/tiptap-ui/header-footer';
import { Attachment } from 'containers/Tiptap/components/tiptap-node/attachment-node';
import { RiskAssessmentsNode } from 'containers/Tiptap/components/tiptap-node/risk-assessments-node';
import { RiskAssessmentNode } from 'containers/Tiptap/components/tiptap-node/risk-assessment-node';

// --- Table Node ---
import { TableKit } from 'containers/Tiptap/components/tiptap-node/table-node/extensions/table-node-extension';
import { TableHandleExtension } from 'containers/Tiptap/components/tiptap-node/table-node/extensions/table-handle';
import { TableHandle } from 'containers/Tiptap/components/tiptap-node/table-node/ui/table-handle/table-handle';
import { TableSelectionOverlay } from 'containers/Tiptap/components/tiptap-node/table-node/ui/table-selection-overlay';
import { TableCellHandleMenu } from 'containers/Tiptap/components/tiptap-node/table-node/ui/table-cell-handle-menu';
import { TableExtendRowColumnButtons } from 'containers/Tiptap/components/tiptap-node/table-node/ui/table-extend-row-column-button';

// --- Tiptap UI ---
import { EmojiDropdownMenu } from 'containers/Tiptap/components/tiptap-ui/emoji-dropdown-menu';
import { ALL_SLASH_MENU_ITEMS, SlashDropdownMenu } from 'containers/Tiptap/components/tiptap-ui/slash-dropdown-menu';
import { AiMenu } from 'containers/Tiptap/components/tiptap-ui/ai-menu';
import { DragHandle as DragHandleUI } from 'containers/Tiptap/components/tiptap-ui/drag-handle';
import MainToolbarContent from 'containers/Tiptap/components/tiptap-ui/MainToolbarContent/MainToolbarContent';

// --- Contexts ---
import { AppProvider } from 'containers/Tiptap/contexts/app-context';
import { UserProvider, useUser } from 'containers/Tiptap/contexts/user-context';
import { CollabProvider, useCollab } from 'containers/Tiptap/contexts/collab-context';
import { AiProvider, useAi } from 'containers/Tiptap/contexts/ai-context';
import { useAutoCleanStaleAi } from 'containers/Tiptap/hooks/use-auto-clean-stale-ai';
import { useDocumentSettingsSync } from 'containers/Tiptap/hooks/use-document-settings-sync';
import { useResolveHeaderFooterImages } from 'containers/Tiptap/hooks/use-resolve-header-footer-images';
import { useResolveHeaderFooterVariables } from 'containers/Tiptap/hooks/use-resolve-header-footer-variables';
import { useDocumentVariables } from 'containers/Tiptap/hooks/use-document-variables';
import { useCloseOverlayOnOutsideClick } from 'containers/Tiptap/hooks/use-close-overlay-on-outside-click';
import { revertBodyAndSettings } from 'containers/Tiptap/lib/version-preview';

// --- Store ---
import { useStore } from 'utils/hooks/store';
import API from 'utils/api';
import { DocumentAccessLevel } from 'utils/api/modules/entities/entities.types';
import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';
import { useThreads } from 'containers/Tiptap/components/tiptap-templates/hooks/useThreads';
import { SidebarEnum } from 'containers/Tiptap/components/tiptap-templates/types';
import { DocumentHeader } from 'containers/DocumentsDetails/DocumentHeader/DocumentHeader';
import { ThreadsProvider } from 'containers/Tiptap/components/tiptap-templates/ThreadContext';
import { EditorSidebar } from 'Tiptap/components/tiptap-templates/simple-editor/EditorSidebar';
import { EditorModals } from 'Tiptap/components/tiptap-templates/simple-editor/EditorModals';
import { VersionHistoryModal } from 'containers/Tiptap/components/tiptap-ui/VersioningContainer';

// --- Lib ---
import { handleImageUpload, MAX_DOCUMENT_IMAGE_SIZE } from 'containers/Tiptap/lib/tiptap-utils';
import { pagesOptions } from 'containers/Tiptap/lib/pages-options';
import { colors } from 'theme/colors';
import TiptapTextSelectionToolbar from 'components/TextSelectionToolbar/TiptapTextSelectionToolbar';
import { customNodes } from 'Tiptap/editor-config/extensions/export-custom-nodes';
import { watchPreviewContent } from '@tiptap-pro/extension-snapshot';
import isEqual from 'lodash/isEqual';

// --- Styles ---
import 'containers/Tiptap/components/tiptap-templates/notion-like/notion-like-editor.scss';
// --- Content ---
import { NotionEditorHeader } from 'containers/Tiptap/components/tiptap-templates/notion-like/notion-like-editor-header';
import { MobileToolbar } from 'containers/Tiptap/components/tiptap-templates/notion-like/notion-like-editor-mobile-toolbar';
import { SetupErrorMessage } from 'containers/Tiptap/components/setup-error-message';
import { TocSidebar } from 'containers/Tiptap/components/tiptap-node/toc-node';
import { TocProvider, useToc } from 'containers/Tiptap/components/tiptap-node/toc-node/context/toc-context';
import { ListNormalizationExtension } from 'containers/Tiptap/components/tiptap-extension/list-normalization-extension';
import {
  readStoredZoomPercent,
  writeStoredZoomPercent,
} from 'containers/Tiptap/components/tiptap-ui/zoom-controls/zoom-persistence';

// oxlint-disable-next-line typescript/no-explicit-any
const normalizeNode = (node: any): any => {
  if (!node || typeof node !== 'object') return node;

  const { attrs, content, marks, ...rest } = node;
  const normalized: Record<string, unknown> = { ...rest };

  if (attrs) {
    const { textAlign, id, 'data-toc-id': _tocId, ...otherAttrs } = attrs;
    const cleanAttrs: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(otherAttrs)) {
      if (value != null) {
        cleanAttrs[key] = value;
      }
    }
    if (textAlign != null) {
      cleanAttrs.textAlign = textAlign;
    }
    if (Object.keys(cleanAttrs).length > 0) {
      normalized.attrs = cleanAttrs;
    }
  }

  if (marks && Array.isArray(marks) && marks.length > 0) {
    normalized.marks = marks.map(normalizeNode);
  }

  if (content && Array.isArray(content) && content.length > 0) {
    let normalizedContent = content.map(normalizeNode);

    // Strip trailing empty paragraphs from the doc root — TipTap keeps one for
    // cursor placement but snapshots don't store it, causing false mismatches.
    if (rest.type === 'doc') {
      while (
        normalizedContent.length > 0 &&
        normalizedContent[normalizedContent.length - 1].type === 'paragraph' &&
        !normalizedContent[normalizedContent.length - 1].content
      ) {
        normalizedContent = normalizedContent.slice(0, -1);
      }
    }

    if (normalizedContent.length > 0) {
      normalized.content = normalizedContent;
    }
  }

  return normalized;
};

// oxlint-disable-next-line typescript/no-explicit-any
const isContentEqual = (a: any, b: any): boolean => isEqual(normalizeNode(a), normalizeNode(b));

function CellMenu({
  editor,
  onResizeStart,
}: {
  editor?: Editor | null;
  onResizeStart?: (handle: string) => (event: MouseEvent) => void;
}): JSX.Element {
  return <TableCellHandleMenu editor={editor} onMouseDown={(e) => onResizeStart?.('br')(e)} />;
}

export interface NotionEditorProps {
  room: string;
  placeholder?: string;
  initialLastSavedAt?: string | null;
  initialLastSavedBy?: { uuid: string; name: string } | null;
}

export interface EditorProviderProps {
  provider: TiptapCollabProvider;
  ydoc: YDoc;
  placeholder?: string;
  aiToken: string | null;
  entityId: string;
  initialLastSavedAt?: string | null;
  initialLastSavedBy?: { uuid: string; name: string } | null;
}

/**
 * Loading spinner component shown while connecting to the notion server
 */
export function LoadingSpinner({ text = 'Connecting...' }: { text?: string }): JSX.Element {
  return (
    <div className="spinner-container">
      <div className="spinner-content">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <div className="spinner-loading-text">{text}</div>
      </div>
    </div>
  );
}

const slashMenuItemsWithoutRiskAssessment = ALL_SLASH_MENU_ITEMS.filter(
  (item) => item !== 'risk_assessment'
);

/**
 * EditorContent component that renders the actual editor
 */
export function EditorContentArea(): JSX.Element | null {
  const { editor } = useContext(EditorContext)!;
  const { aiGenerationIsLoading, aiGenerationIsSelection, aiGenerationHasMessage, isDragging } =
    useUiEditorState(editor);
  const { session, documentsDetails } = useStore();
  const isViewOnly = documentsDetails.accessLevel === DocumentAccessLevel.View;

  // Selection based effect to handle AI generation acceptance
  useEffect(() => {
    if (!editor) return;

    if (!aiGenerationIsLoading && aiGenerationIsSelection && aiGenerationHasMessage) {
      editor.chain().focus().aiAccept().run();
      editor.commands.resetUiState();
    }
  }, [aiGenerationHasMessage, aiGenerationIsLoading, aiGenerationIsSelection, editor]);

  useScrollToHash();

  if (!editor) {
    return null;
  }

  const riskAssessmentEnabled = session.user?.riskAssessmentEnabled ?? false;
  const slashMenuConfig = riskAssessmentEnabled ? undefined : { enabledItems: slashMenuItemsWithoutRiskAssessment };

  return (
    <>
      <EditorContent
        editor={editor}
        role="presentation"
        className="notion-like-editor-content"
        style={{
          cursor: isDragging ? 'grabbing' : 'auto',
        }}
      />
      {!isViewOnly && (
        <>
          <DragHandleUI editor={editor} />
          <AiMenu />
          <EmojiDropdownMenu />
          <SlashDropdownMenu config={slashMenuConfig} />
          <TiptapTextSelectionToolbar editor={editor} />
          {createPortal(<MobileToolbar />, document.body)}
        </>
      )}
    </>
  );
}

/**
 * Component that creates and provides the editor instance
 */
export const EditorProvider = observer(function EditorProvider(props: EditorProviderProps): JSX.Element {
  const {
    provider,
    ydoc,
    placeholder = 'Start writing...',
    aiToken,
    entityId,
    initialLastSavedAt,
    initialLastSavedBy,
  } = props;
  const params = useParams<{ entityId: string; shareId?: string }>();

  const { user } = useUser();
  const { setTocContent } = useToc();
  const { notification, session, documentsDetails } = useStore();
  const editorStore = useEditorStore();
  const builtViewModeRef = useRef(editorStore.viewMode);

  const isViewOnly = documentsDetails.accessLevel === DocumentAccessLevel.View;

  const [isSynced, setIsSynced] = React.useState(false);

  const reflowTick = editorStore.documentTabReflowTick;

  const hasCheckedUnpublishedChanges = useRef(false);

  const initialZoomPercentRef = useRef(readStoredZoomPercent(session.user?.uuid, entityId));

  useEffect(() => {
    initialZoomPercentRef.current = readStoredZoomPercent(session.user?.uuid, entityId);
    editorStore.setZoomPercent(initialZoomPercentRef.current);
  }, [editorStore, entityId, session.user?.uuid]);

  useEffect(
    () =>
      reaction(
        () => editorStore.zoomPercent,
        (zoomPercent) => writeStoredZoomPercent(session.user?.uuid, entityId, zoomPercent)
      ),
    [editorStore, entityId, session.user?.uuid]
  );

  useEffect(() => {
    editorStore.setCurrentEntityId(entityId);
    hasCheckedUnpublishedChanges.current = false;
    return () => {
      editorStore.reset();
    };
  }, [editorStore, entityId]);

  useEffect(() => {
    session.subscribeToDocumentChannel(entityId);
    return () => {
      session.unsubscribeFromDocumentChannel(entityId);
    };
  }, [entityId, session]);

  useEffect(() => {
    const handleSynced = (): void => setIsSynced(true);
    provider.on('synced', handleSynced);
    return () => {
      provider.off('synced', handleSynced);
    };
  }, [provider]);

  const threadsRef = useRef<ReturnType<typeof useThreads>['threads']>([]);

  const editor = useEditor({
    editable: !isViewOnly,
    immediatelyRender: false,
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        class: 'notion-like-editor',
      },
    },
    extensions: [
      StarterKit.configure({
        undoRedo: false,
        horizontalRule: false,
        dropcursor: {
          width: 1,
          color: '#3b82f6',
        },
        link: { openOnClick: true },
        code: false,
      }),
      InlineCode,
      HorizontalRule,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Collaboration.configure({ document: ydoc }),
      CollaborationCaret.configure({
        provider,
        user: {
          name: user?.name || 'Anonymous',
          color: user?.color || '#f783ac',
        },
      }),
      Snapshot.configure({
        provider,
        onUpdate(payload) {
          editorStore.setSnapshotState({
            versions: payload.versions,
            currentVersion: payload.currentVersion,
            latestVersion: payload.version,
            autoVersioning: payload.versioningEnabled,
          });
        },
      }),
      CommentsKit.configure({
        provider,
        onClickThread: (id: string | null) => {
          const isResolved = threadsRef.current.find((t) => t.id === id)?.resolvedAt;

          if (!id || isResolved) {
            setSelectedThread(null);
            editor?.chain().unselectThread().run();
            return;
          }

          setSelectedThread(id);
          editorStore.setActiveSidebar(SidebarEnum.Comments);
          editor?.chain().selectThread({ id, updateSelection: false }).run();
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyNodeClass: 'is-empty with-slash',
      }),
      Emoji.configure({
        emojis: gitHubEmojis.filter((emoji) => !emoji.name.includes('regional')),
        forceFallbackImages: true,
      }),
      TableKit.configure({
        table: {
          resizable: true,
          cellMinWidth: 120,
        },
      }),
      ...(editorStore.viewMode === DocumentViewMode.Pages
        ? [
            Pages.configure(
              pagesOptions({
                editableHeaderFooter: !isViewOnly,
                initialZoomPercent: initialZoomPercentRef.current,
                onZoomPercentChange: editorStore.setZoomPercent,
              })
            ),
            PageBreak,
          ]
        : []),
      NodeBackground.configure({
        types: [
          'paragraph',
          'heading',
          'blockquote',
          'taskList',
          'bulletList',
          'orderedList',
          'tableCell',
          'tableHeader',
          'tocNode',
        ],
      }),
      NodeAlignment,
      TextStyle,
      Superscript,
      Subscript,
      Color,
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Selection,
      Image,
      TableOfContents.configure({
        getIndex: getHierarchicalIndexes,
        onUpdate(content) {
          setTocContent(content);
        },
      }),
      TableHandleExtension,
      ListNormalizationExtension,
      ImageUploadNode.configure({
        accept: 'image/*',
        maxSize: MAX_DOCUMENT_IMAGE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error('Upload failed:', error),
      }),
      FileDropExtension.configure({
        entityId,
        enqueueErrorSnackbar: notification.enqueueErrorSnackbar,
      }),
      Attachment,
      RiskAssessmentsNode,
      RiskAssessmentNode,
      UniqueID.configure({
        types: [
          'table',
          'paragraph',
          'bulletList',
          'orderedList',
          'taskList',
          'heading',
          'blockquote',
          'codeBlock',
          'tocNode',
          'riskMatrix',
        ],
        filterTransaction: (transaction) => !isChangeOrigin(transaction),
      }),
      Typography,
      Mathematics,
      UiState,
      TocNode.configure({
        topOffset: 100,
      }),
      Ai.configure({
        token: aiToken || undefined,
        autocompletion: false,
        showDecorations: true,
        hideDecorationsOnStreamEnd: false,
        onLoading: (context) => {
          context.editor.commands.aiGenerationSetIsLoading(true);
          context.editor.commands.aiGenerationHasMessage(false);
        },
        onChunk: (context) => {
          context.editor.commands.aiGenerationSetIsLoading(true);
          context.editor.commands.aiGenerationHasMessage(true);
        },
        onSuccess: (context) => {
          const hasMessage = !!context.response;
          context.editor.commands.aiGenerationSetIsLoading(false);
          context.editor.commands.aiGenerationHasMessage(hasMessage);
        },
      }),
      createConvertKitExtension(),
      BulletListFormats,
      NumberingFormatsSync.configure({ ydoc }),
      AiChanges.configure({
        getCustomDecorations({ change, isSelected, getDefaultDecorations }) {
          const decorations = getDefaultDecorations();
          if (isSelected) {
            decorations.push(Decoration.widget(change.newRange.to, () => document.createElement('span')));
          }
          return decorations;
        },
      }),
      ExportDocx.configure({
        customNodes,
        numberingFormats,
        onCompleteExport: () => {},
      }),
    ],
  });

  const {
    threads,
    selectedThread,
    setSelectedThread,
    selectThreadInEditor,
    deleteThread,
    resolveThread,
    unresolveThread,
    updateComment,
    onHoverThread,
    onLeaveThread,
  } = useThreads(provider, editor);

  useAutoCleanStaleAi(editor);

  useEffect(() => {
    editorStore.setEditor(editor);
    editorStore.setYDoc(ydoc ?? null);
    return () => {
      editorStore.setEditor(null);
      editorStore.setYDoc(null);
    };
  }, [editor, editorStore, ydoc]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isViewOnly);
    if (typeof (editor.commands as Record<string, unknown>).setHeaderEditable === 'function') {
      editor.chain().setHeaderEditable(!isViewOnly).setFooterEditable(!isViewOnly).run();
    }
  }, [editor, isViewOnly]);

  useDocumentSettingsSync({ editor, ydoc, isSynced, editorStore, reflowTick });
  const documentVariables = useDocumentVariables();
  useResolveHeaderFooterImages(editor, params.shareId);
  useResolveHeaderFooterVariables(editor, documentVariables);
  useCloseOverlayOnOutsideClick(editor);

  // Seed a new document with .docx import content once collab `isSynced` (else
  // y-prosemirror clobbers it); the ref keys by entityId so each import seeds once.
  const lastSeededEntityIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editor) return undefined;
    if (lastSeededEntityIdRef.current === entityId) return undefined;
    const pending = documentsDetails.pendingInitialContent;
    if (!pending || pending.entityId !== entityId) return undefined;

    // The view is created lazily (`immediatelyRender: false`); touching it
    // before mount throws, so guard the access.
    const isViewMounted = (): boolean => {
      try {
        return Boolean(editor.view?.dom);
      } catch {
        return false;
      }
    };

    const applySeed = (): void => {
      if (lastSeededEntityIdRef.current === entityId) return;
      const needsModeSwitch = pending.viewMode !== builtViewModeRef.current;
      if (pending.viewMode === DocumentViewMode.Pages && ydoc) {
        writeDocumentSettings(ydoc, { viewMode: DocumentViewMode.Pages });
        try {
          writeImportedHeaderFooter(ydoc, pending.headerFooter);
        } catch (error) {
          Logger.error('Failed to seed imported headers and footers', error);
        }
      }
      // Switching remounts this editor; the remount seeds against the new mode's schema.
      if (needsModeSwitch) {
        editorStore.setViewMode(pending.viewMode);
        return;
      }
      lastSeededEntityIdRef.current = entityId;
      try {
        // Persisted to the ydoc
        if (pending.numberingFormats.length > 0 && ydoc) {
          writeNumberingFormats(ydoc, pending.numberingFormats);
        }
        // Prune against the live schema (it varies with viewMode extensions).
        editor.commands.setContent(pruneToSchema(pending.json, editor.schema));
      } catch (error) {
        Logger.error('Failed to seed imported content', error);
      } finally {
        documentsDetails.clearPendingInitialContent();
      }
    };

    const seedNow = (): void => {
      if (isViewMounted()) {
        applySeed();
        return;
      }
      // Otherwise wait for Tiptap's `mount` event before seeding.
      const onMount = (): void => {
        editor.off('mount', onMount);
        applySeed();
      };
      editor.on('mount', onMount);
    };

    if (isSynced) {
      seedNow();
      return undefined;
    }
    const timer = setTimeout(seedNow, 1500);
    return () => clearTimeout(timer);
  }, [editor, isSynced, entityId, documentsDetails, documentsDetails.pendingInitialContent, editorStore, ydoc]);

  React.useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  // Disable auto-versioning once connected (we use manual versioning only)
  const autoVersioningDisabledRef = useRef(false);

  useEffect(() => {
    if (isViewOnly || !editor || autoVersioningDisabledRef.current || !editorStore.snapshotState.autoVersioning) {
      return undefined;
    }

    const disableAutoVersioning = ({ transaction }: { transaction: { getMeta: (key: string) => unknown } }): void => {
      if (autoVersioningDisabledRef.current || transaction.getMeta('y-sync$')) return;
      editor.commands.toggleVersioning();
      autoVersioningDisabledRef.current = true;
    };

    editor.on('update', disableAutoVersioning);
    return () => {
      editor.off('update', disableAutoVersioning);
    };
  }, [isViewOnly, editor, editorStore.snapshotState.autoVersioning]);

  // Load the version list on mount — the snapshot extension doesn't fetch it on connect, so
  // the publish flow otherwise can't know the version count (and next name) after a reload.
  const versionsFetchedRef = useRef(false);

  useEffect(() => {
    if (editor && !versionsFetchedRef.current) {
      editor.commands.fetchVersions();
      versionsFetchedRef.current = true;
    }
  }, [editor]);

  // Track unpublished changes
  useEffect(() => {
    if (!editorStore.snapshotReady || !isSynced || hasCheckedUnpublishedChanges.current || !editor || !provider) {
      return undefined;
    }

    const { versions } = editorStore;

    if (versions.length === 0) {
      const content = editor.getJSON();
      const hasContent = content.content?.some(
        (node) => node.type !== 'paragraph' || (node.content && node.content.length > 0)
      );

      hasCheckedUnpublishedChanges.current = true;

      if (hasContent) {
        editorStore.setHasUnpublishedChanges(true);
      }
      return undefined;
    }

    hasCheckedUnpublishedChanges.current = true;

    const latestVersion = versions[versions.length - 1];
    const currentContent = editor.getJSON();

    provider.sendStateless(
      JSON.stringify({
        action: 'version.preview',
        version: latestVersion.version,
      })
    );

    const unbind = watchPreviewContent(provider, (content) => {
      if (!isContentEqual(currentContent, content)) {
        editorStore.setHasUnpublishedChanges(true);
      }
      unbind();
    });

    return () => {
      unbind();
    };
  }, [editorStore.snapshotReady, isSynced, editor, provider]);

  useEffect(() => {
    if (!editor) return undefined;

    const handleUpdate = ({
      transaction,
    }: {
      transaction: { docChanged: boolean; getMeta: (key: string) => unknown };
    }): void => {
      if (
        !editorStore.snapshotReady ||
        !hasCheckedUnpublishedChanges.current ||
        !transaction.docChanged ||
        transaction.getMeta('y-sync$')
      )
        return;
      editorStore.setHasUnpublishedChanges(true);
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, editorStore]);

  // Versioning handlers
  const pendingVersionRef = useRef<{ versionNumber: number } | null>(null);
  const pendingVersionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // `version.created` is the reliable publish signal; versions.length lags on the first version.
  useEffect(() => {
    if (!provider) return undefined;

    const handleStateless = (event: { payload: string }): void => {
      let parsed: { event?: string; version?: number };
      try {
        parsed = JSON.parse(event.payload);
      } catch {
        return;
      }
      if (parsed.event !== 'version.created') return;

      // Refetch so versions.length is right for the next version's name (first version loads async).
      editor?.commands.fetchVersions();

      editorStore.setHasUnpublishedChanges(false);

      if (!isViewOnly && typeof parsed.version === 'number') {
        API.entities
          .setDocumentTiptapVersion(entityId, parsed.version)()
          .catch(() => {
            notification.enqueueErrorSnackbar(
              'Could not sync document version — acknowledgement requests on this document may use a stale version. Re-publish to retry.'
            );
          });
      }

      if (pendingVersionRef.current) {
        if (pendingVersionTimeoutRef.current) {
          clearTimeout(pendingVersionTimeoutRef.current);
          pendingVersionTimeoutRef.current = null;
        }
        const { versionNumber } = pendingVersionRef.current;
        pendingVersionRef.current = null;
        editorStore.closePublishModal();
        editorStore.setIsPublishing(false);
        notification.enqueueSuccessSnackbar(`Version ${versionNumber} published`);
      }
    };

    provider.on('stateless', handleStateless);
    return () => {
      provider.off('stateless', handleStateless);
    };
  }, [provider, editor, editorStore, notification, entityId, isViewOnly]);

  useEffect(
    () => () => {
      if (pendingVersionTimeoutRef.current) {
        clearTimeout(pendingVersionTimeoutRef.current);
      }
    },
    []
  );

  const handleOpenSaveVersionModal = useCallback(() => {
    if (!editor || editorStore.isPublishing) return;
    // Version numbers are 0-indexed but names are 1-indexed, so the next name is the current
    // count + 1 — use versions.length, not latestVersion (which would yield "v1" again).
    const nextVersionNumber = editorStore.versions.length + 1;
    editorStore.openPublishModal(nextVersionNumber);
  }, [editor, editorStore]);

  const handleSaveVersion = useCallback(
    (versionName: string) => {
      if (!editor || editorStore.isPublishing) return;

      editorStore.setIsPublishing(true);

      try {
        // force=true: always create a version on explicit publish, even if auto-versioning
        // already snapshotted identical content — otherwise it silently no-ops and times out.
        const success = editor.commands.saveVersion(versionName, true);

        if (!success) {
          notification.enqueueErrorSnackbar('Failed to save version. Please try again.');
          editorStore.setIsPublishing(false);
          return;
        }

        pendingVersionRef.current = { versionNumber: editorStore.publishVersionNumber };

        pendingVersionTimeoutRef.current = setTimeout(() => {
          if (pendingVersionRef.current) {
            pendingVersionRef.current = null;
            editorStore.setIsPublishing(false);
            notification.enqueueErrorSnackbar('Failed to save version. Please try again.');
          }
        }, 5000);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        notification.enqueueErrorSnackbar(`Failed to save version: ${errorMessage}`);
        editorStore.setIsPublishing(false);
      }
    },
    [editor, editorStore, notification]
  );

  const handleRevertVersion = (version: number): void => {
    const versionData = editorStore.versions.find((v) => v.version === version);
    revertBodyAndSettings(provider, version, versionData?.name || `Version ${version}`);
  };

  if (!editor || !isSynced) {
    return <LoadingSpinner text={!editor ? 'Connecting...' : 'Loading document...'} />;
  }

  return (
    <div className="notion-like-editor-wrapper">
      <EditorContext.Provider value={{ editor }}>
        <FileUploadProvider entityId={entityId} enqueueErrorSnackbar={notification.enqueueErrorSnackbar}>
          <ThreadsProvider
            threads={threads}
            selectedThreads={editor.storage.comments?.focusedThreads ?? []}
            selectedThread={selectedThread}
            onClickThread={selectThreadInEditor}
            onDeleteThread={deleteThread}
            onResolveThread={resolveThread}
            onUnresolveThread={unresolveThread}
            onUpdateComment={updateComment}
            onHoverThread={onHoverThread}
            onLeaveThread={onLeaveThread}
            setSelectedThread={setSelectedThread}
          >
            <NotionEditorHeader
              editorStore={editorStore}
              onPublish={handleOpenSaveVersionModal}
              currentUserUuid={session.user?.uuid}
              initialLastSavedAt={initialLastSavedAt ?? null}
              initialLastSavedBy={initialLastSavedBy ?? null}
            />
            <DocumentHeader />
            {!isViewOnly && <MainToolbarContent />}
            <div
              className="notion-like-editor-layout"
              data-view-mode={editorStore.viewMode}
            >
              <EditorContentArea />
              {editorStore.viewMode === DocumentViewMode.Pages && <PageEdgeStyles />}
              {!isViewOnly && editorStore.viewMode === DocumentViewMode.Pages && <HeaderFooterEditing editor={editor} />}
              <TocSidebar topOffset={100} />
            </div>

            <EditorSidebar
              provider={provider}
              threads={threads}
              activeSidebar={editorStore.activeSidebar}
              setActiveSidebar={editorStore.setActiveSidebar}
              setSelectedThread={setSelectedThread}
            />

            <EditorModals
              shareModalOpen={editorStore.shareModalOpen}
              entityId={params.entityId || entityId}
              onCloseShareModal={editorStore.closeShareModal}
              hasPublishedVersion={editorStore.versions.length > 0}
              saveVersionModalOpen={editorStore.publishModalOpen}
              onCloseSaveVersionModal={editorStore.closePublishModal}
              onConfirmSaveVersion={handleSaveVersion}
              isSavingVersion={editorStore.isPublishing}
              nextVersionNumber={editorStore.publishVersionNumber}
            />

            <VersionHistoryModal
              provider={provider}
              ydoc={ydoc}
              viewMode={editorStore.viewMode}
              versions={editorStore.versions}
              isOpen={editorStore.activeSidebar === SidebarEnum.Versioning}
              onClose={editorStore.closeSidebar}
              onRevert={handleRevertVersion}
              hasUnpublishedChanges={editorStore.hasUnpublishedChanges}
            />
          </ThreadsProvider>

          {!isViewOnly && (
            <>
              <TableExtendRowColumnButtons />
              <TableHandle />
              <TableSelectionOverlay showResizeHandles cellMenu={CellMenu} />
            </>
          )}
        </FileUploadProvider>
      </EditorContext.Provider>
    </div>
  );
});

/**
 * Full editor with all necessary providers, ready to use with just a room ID
 */
export function NotionEditor({
  room,
  placeholder = 'Start writing...',
  initialLastSavedAt,
  initialLastSavedBy,
}: NotionEditorProps): JSX.Element {
  // Ensure light theme is always applied
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  return (
    <UserProvider>
      <AppProvider>
        <CollabProvider room={room}>
          <AiProvider>
            <TocProvider>
              <NotionEditorContent
                placeholder={placeholder}
                entityId={room}
                initialLastSavedAt={initialLastSavedAt}
                initialLastSavedBy={initialLastSavedBy}
              />
            </TocProvider>
          </AiProvider>
        </CollabProvider>
      </AppProvider>
    </UserProvider>
  );
}

/**
 * Internal component that handles the editor loading state
 */
export function NotionEditorContent({
  placeholder,
  entityId,
  initialLastSavedAt,
  initialLastSavedBy,
}: {
  placeholder?: string;
  entityId: string;
  initialLastSavedAt?: string | null;
  initialLastSavedBy?: { uuid: string; name: string } | null;
}): JSX.Element {
  const { provider, ydoc, setupError: collabSetupError } = useCollab();
  const { aiToken, setupError: aiSetupError } = useAi();

  // Show setup error if either collab or AI setup failed
  if (collabSetupError || aiSetupError) {
    return <SetupErrorMessage aiSetupError={aiSetupError} collabSetupError={collabSetupError} />;
  }

  if (!provider || !aiToken) {
    return <LoadingSpinner />;
  }

  return (
    <EditorProvider
      provider={provider}
      ydoc={ydoc}
      placeholder={placeholder}
      aiToken={aiToken}
      entityId={entityId}
      initialLastSavedAt={initialLastSavedAt}
      initialLastSavedBy={initialLastSavedBy}
    />
  );
}
